/**
 * "Extract network context" — a kebab-menu item (+ result modal) for the
 * Workbench header. Consolidates every room's context into ONE network-wide
 * summary on demand (the reporting view; NOT injected into runs).
 *
 * Smart, per the v1 design:
 *   - Hidden unless the network is RELEASED with ≥1 room (``eligible`` from the API).
 *   - When nothing changed since the last extract (``stale === false``), the item
 *     is shown but DISABLED with "Up to date · <date>" — so the user sees the
 *     feature + when it last ran, and we never spend on a no-op synthesis.
 *
 * Reads: getNetworkContext (eligibility + cached summary + stale flag).
 * Writes: extractNetworkContext (one Haiku call; server staleness-skips anyway).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Network as NetworkIcon, X as XIcon } from 'lucide-react';
import { getNetworkContext, extractNetworkContext } from '@stepflow/ui-shared/services/dataPlaneApi';

function fmtWhen(iso) {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * @param {{ networkId: string, onActivate?: () => void }} props
 *   onActivate — called right before opening the modal (lets the header close its menu).
 */
export default function NetworkContextMenuItem({ networkId, onActivate }) {
  const [info, setInfo] = useState(null); // {eligible, roomCount, summary, stale}
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!networkId) return;
    try {
      const res = await getNetworkContext(networkId);
      setInfo(res);
    } catch {
      setInfo({ eligible: false });
    } finally {
      setLoaded(true);
    }
  }, [networkId]);

  useEffect(() => { refresh(); }, [refresh]);

  const runExtract = useCallback(async () => {
    if (!networkId || extracting) return;
    setExtracting(true);
    setError(null);
    try {
      const res = await extractNetworkContext(networkId, { force: false });
      if (res?.error) setError(res.error);
      setInfo((prev) => ({ ...(prev || {}), summary: res?.summary ?? prev?.summary, stale: false }));
    } catch (e) {
      setError(e?.message || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  }, [networkId, extracting]);

  // Not eligible (network not released as rooms, or load failed) → render nothing.
  if (!loaded || !info?.eligible) return null;

  const summary = info.summary || null;
  const stale = !!info.stale;
  const extractedAt = fmtWhen(summary?.extractedAt);
  // Disabled when nothing changed AND we already have a summary (up to date).
  const upToDate = !stale && !!summary;

  const openModal = () => {
    onActivate?.();
    setModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
        role="menuitem"
        data-testid="network-context-menu-item"
      >
        <NetworkIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0">
          <span className="block">Network context</span>
          <span className="block text-[11px] text-gray-400">
            {upToDate
              ? `Up to date · ${extractedAt}`
              : stale
                ? 'Updates available — extract'
                : summary ? `Extracted ${extractedAt}` : 'Extract a consolidated summary'}
          </span>
        </span>
      </button>

      {modalOpen ? createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl" data-testid="network-context-modal">
            <header className="flex items-start justify-between gap-2 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">Network context</h2>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  A consolidated summary across all {info.roomCount} room{info.roomCount === 1 ? '' : 's'} —
                  what the network as a whole has been doing. Reporting only; not injected into runs.
                </p>
              </div>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
                <XIcon className="h-5 w-5" aria-hidden />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {summary ? (
                <>
                  <div className="mb-2 flex items-center gap-2 text-[11px] text-gray-400">
                    <span>extracted {extractedAt}</span>
                    {typeof summary.points === 'number' ? <span>· {summary.points} pts</span> : null}
                    {stale ? <span className="rounded bg-amber-100 px-1.5 py-px font-medium text-amber-700">updates available</span> : null}
                  </div>
                  <pre className="whitespace-pre-wrap break-words rounded-md bg-indigo-50 px-3 py-2 text-[12px] leading-relaxed text-gray-800 ring-1 ring-indigo-100">
{summary.summary}
                  </pre>
                </>
              ) : (
                <p className="text-[11px] text-gray-500">
                  No network summary yet — extract one to consolidate every room's context.
                </p>
              )}
              {error ? (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">{error}</div>
              ) : null}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={runExtract}
                disabled={extracting || upToDate}
                title={upToDate ? 'Nothing has changed since the last extract.' : undefined}
                data-testid="network-context-extract-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {extracting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                {extracting ? 'Extracting…' : upToDate ? 'Up to date' : summary ? 'Re-extract' : 'Extract'}
              </button>
            </footer>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
